import { useCallback } from 'react'
import { useDispatch } from 'react-redux'

import { useHistory } from 'react-router-dom'
import { clearState } from '../actions/clearActions'
import { useErrorHandling } from '../errors'
import { buildAuthFailureTags } from './authFailureTags'
import useAuthProvider, { defaultAuthParams } from './useAuthProvider'

type LogoutParams = {
  reason?: string
  cause?: any
}

/**
 * Get a callback for calling the authProvider.logout() method,
 * redirect to the login page, and clear the Redux state.
 *
 * @see useAuthProvider
 *
 * @returns {Function} logout callback
 *
 * @example
 *
 * import { useLogout } from 'react-admin';
 *
 * const LogoutButton = () => {
 *     const logout = useLogout();
 *     const handleClick = () => logout();
 *     return <button onClick={handleClick}>Logout</button>;
 * }
 */
const useLogout = (): Logout => {
  const authProvider = useAuthProvider()
  const dispatch = useDispatch()

  /**
   * We need the current location to pass in the router state
   * so that the login hook knows where to redirect to as next route after login.
   *
   * But if we used useLocation to get it, the logout function
   * would be rebuilt each time the user changes location. Consequently, that
   * would force a rerender of all components using this hook upon navigation
   * (CoreAdminRouter for example).
   *
   * To avoid that, we read the location directly from history which is mutable.
   * See: https://reacttraining.com/react-router/web/api/history/history-is-mutable
   */
  const history = useHistory()

  const errorHandling = useErrorHandling()

  const logout = useCallback(
    (params: LogoutParams, redirectTo = defaultAuthParams.loginUrl, recordCurrentPath = true) => {
      // Only emit the "Requesting Logout" Sentry capture (added per #19283 to debug
      // unexpected session ends) when there is actually a session to end. Skipping
      // this in the no-session case avoids two noise sources:
      //   1. The access-denied flow runs clearAuthStateFromLocalStorage *before*
      //      reaching here, so by this point isLoggedIn() is already false; that
      //      capture is the authoritative signal and includes the same context.
      //   2. An unauthenticated user landing on a protected URL triggers
      //      useCheckAuth -> logout({reason: 'check-auth-failure'}) without ever
      //      having had a session, which is expected behaviour, not an error.
      // The local cleanup (authProvider.logout, clearState, history.push) below
      // still runs unconditionally.
      if (authProvider.isLoggedIn()) {
        console.info('Requesting Logout: ', params.reason)
        //@ts-ignore - Error constructor takes 1 argument in es5, 2 in es6
        const error = new Error('Requesting Logout', { cause: params.cause })
        errorHandling.unexpected(error, {
          notify: false, // disable user facing notification
          console: false, // disable console.error
          captureContext: {
            // buildAuthFailureTags pulls endpoint_url, response_status,
            // failure_kind, etc. out of the underlying HttpError so the
            // capture identifies *which* endpoint produced the 401/403.
            tags: { reason: params.reason, ...buildAuthFailureTags(params.cause) },
          },
        })
      }

      return authProvider.logout(params).then((redirectToFromProvider) => {
        const redirect = redirectToFromProvider || redirectTo

        // no need to redirect if already on login
        if (history?.location.pathname === redirect) {
          return
        }

        dispatch(clearState())
        history.push({
          pathname: redirect,
          state: recordCurrentPath
            ? {
                nextPathname: history.location && history.location.pathname,
                nextSearch: history.location && history.location.search,
              }
            : {},
        })

        return redirect
      })
    },
    [authProvider, history, dispatch]
  )

  const logoutWithoutProvider = useCallback(
    (_) => {
      history.push({
        pathname: defaultAuthParams.loginUrl,
        state: {
          nextPathname: history.location && history.location.pathname,
        },
      })
      dispatch(clearState())
      return Promise.resolve()
    },
    [dispatch, history]
  )

  return authProvider ? logout : logoutWithoutProvider
}

/**
 * Log the current user out by calling the authProvider.logout() method,
 * and redirect them to the login screen.
 *
 * @param {Object} params The parameters to pass to the authProvider
 * @param {string} redirectTo The path name to redirect the user to (optional, defaults to login)
 * @param {boolean} params The parameter decide whether pass nextPathname
 * @return {Promise} The authProvider response
 */
type Logout = (params?: any, redirectTo?: string, recordCurrentPath?: boolean) => Promise<any>

export default useLogout
